import AppConfig from '../../utils/appconfig';

// Kontrakt z extra_php/gallery_public.php

export type GalleryVariant = 'thumb' | 'webp' | 'orig';

/** Szablony układu zdjęć (CFG_3) — kontrakt z gallery_public.php */
export const GALLERY_LAYOUTS = ['grid', 'masonry', 'hero', 'rows', 'filmstrip', 'justified'] as const;
export type GalleryLayout = typeof GALLERY_LAYOUTS[number];

export const CFG_KEYS = ['CFG_1', 'CFG_2', 'CFG_3', 'CFG_4', 'CFG_5'] as const;
export type GalleryCfg = Record<typeof CFG_KEYS[number], string>;

export interface GalleryCrumb {
    name: string;
    path: string;
}

export interface GalleryPhoto {
    name: string;
    path: string;
    /** mtime pliku — w URL-u daje wieczny cache i unieważnia go po podmianie pliku */
    m: number;
}

export interface GalleryAlbum {
    name: string;
    path: string;
    /** zdjęcia bezpośrednio w albumie (bez podalbumów) */
    photoCount: number;
    albumCount: number;
    cover: { path: string; m: number } | null;
    cfg: GalleryCfg;
    layout?: GalleryLayout;
}

export interface GalleryFolder {
    path: string;
    name: string;
    parent: string | null;
    breadcrumb: GalleryCrumb[];
    cfg: GalleryCfg;
    cfgSource: string | null;
    cfgInherited: boolean;
    /** efektywny szablon zdjęć (CFG_3 + dziedziczenie) */
    layout?: GalleryLayout;
    albums: GalleryAlbum[];
    photos: GalleryPhoto[];
}

export function normalizeLayout(value: unknown): GalleryLayout {
    if (typeof value === 'string' && (GALLERY_LAYOUTS as readonly string[]).includes(value)) {
        return value as GalleryLayout;
    }
    return 'grid';
}

export class GalleryApiError extends Error {
    status: number;

    constructor(message: string, status: number) {
        super(message);
        this.status = status;
        Object.setPrototypeOf(this, GalleryApiError.prototype);
    }

    get isNotFound(): boolean {
        return this.status === 404 || this.status === 400;
    }
}

const endpoint = (): string => `${AppConfig.galleryApiPath}/gallery_public.php`;

export const mediaUrl = (path: string, variant: GalleryVariant, m?: number): string =>
    `${endpoint()}?action=media&v=${variant}&f=${encodeURIComponent(path)}${m ? `&m=${m}` : ''}`;

export async function fetchFolder(path: string): Promise<GalleryFolder> {
    let res: Response;
    try {
        res = await fetch(`${endpoint()}?action=list&path=${encodeURIComponent(path)}`, {
            headers: { Accept: 'application/json' },
        });
    } catch {
        throw new GalleryApiError('Nie udało się połączyć z serwerem.', 0);
    }

    let body: any = null;
    try {
        body = await res.json();
    } catch {
        /* odpowiedź nie jest JSON-em */
    }

    if (!res.ok) {
        throw new GalleryApiError(body?.error || `Błąd serwera (HTTP ${res.status}).`, res.status);
    }
    if (!body || !Array.isArray(body.albums) || !Array.isArray(body.photos)) {
        throw new GalleryApiError('Nieprawidłowa odpowiedź serwera.', 502);
    }
    return body as GalleryFolder;
}

// ---- adresy w aplikacji ----------------------------------------------------

/** "a/b" -> "/galeria/a/b" (każdy segment kodowany osobno, ukośniki zostają) */
export const galleryRoute = (path: string): string =>
    '/galeria' + path.split('/').filter(Boolean).map((s) => '/' + encodeURIComponent(s)).join('');

/** wartość splata z routera -> ścieżka folderu bez zbędnych ukośników */
export const folderPathFromRoute = (splat: string | undefined): string =>
    (splat || '').split('/').filter(Boolean).join('/');

export const crumbsFromPath = (path: string): GalleryCrumb[] => {
    const crumbs: GalleryCrumb[] = [];
    let acc = '';
    path.split('/').filter(Boolean).forEach((part) => {
        acc = acc ? `${acc}/${part}` : part;
        crumbs.push({ name: part, path: acc });
    });
    return crumbs;
};

export const parentPath = (path: string): string => {
    const i = path.lastIndexOf('/');
    return i === -1 ? '' : path.slice(0, i);
};
